import {
	addDoc,
	collection,
	doc,
	getDocs,
	limit,
	orderBy,
	query,
	updateDoc,
	where,
} from "firebase/firestore";
import { auth, db } from "initFirebase";
import { getGenerationNameById } from "./GenerationService";
import emailjs from "emailjs-com";
import { FacturapiTax, InvoiceTaxConfig } from "./invoiceTaxes";

// La llave de Facturapi ya no vive en el navegador: todo pasa por la función
// de Netlify netlify/functions/facturapi.js, que valida la sesión del usuario.
const FACTURAPI_FUNCTION_URL = "/.netlify/functions/facturapi";

async function callFacturapi(action: string, payload: Record<string, any> = {}) {
	const currentUser = auth.currentUser;
	if (!currentUser) throw new Error("Necesitas iniciar sesión");
	const token = await currentUser.getIdToken();
	const response = await fetch(FACTURAPI_FUNCTION_URL, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify({ action, ...payload }),
	});
	const body = await response.json().catch(() => ({}));
	if (!response.ok) {
		const error: any = new Error(body.error || `Error ${response.status}`);
		error.status = response.status;
		throw error;
	}
	return body;
}

async function fetchInvoiceFile(invoiceApiId: string, format: string): Promise<Blob> {
	const { base64, contentType } = await callFacturapi("download", { invoiceApiId, format });
	const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
	return new Blob([bytes], { type: contentType });
}

const paymentFormText = {
	"01": "Efectivo",
	"02": "Cheque nominativo",
	"03": "Transferencia electrónica de fondos",
	"04": "Tarjeta de crédito",
	"05": "Monedero electrónico",
	"06": "Dinero electrónico",
	"08": "Vales de despensa",
	"12": "Dación en pago",
	"13": "Pago por subrogación",
	"14": "Pago por consignación",
	"15": "Condonación",
	"17": "Compensación",
	"23": "Novación",
	"24": "Confusión",
	"25": "Remisión de deuda",
	"26": "Prescripción o caducidad",
	"27": "A satisfacción del acreedor",
	"28": "Tarjeta de débito",
	"29": "Tarjeta de servicios",
	"30": "Aplicación de anticipos",
	"31": "Intermediario pagos",
	"99": "Por definir",
};
export interface FirebaseDate {
	nanoseconds: number;
	seconds: number;
}

const dateOptions: any = {
	day: "numeric",
	month: "long",
	year: "numeric",
};

export interface InvoiceData {
	createdAt: Date; // Date of the creation of the invoice doc in firebase, despite any means of creation
	ApiId?: string;
	userId?: string;
	userGen?: string; // User generation at the moment of the request of invoice
	userGenName?: string;
	apiCreatedAt?: Date; // register date when generating, created_at (invoice object)
	requestDate?: Date | FirebaseDate; // Date that this invoice was requested by the user
	issueDate?: Date; // Fecha de expedicion, date (invoice object)
	acceptedDate?: Date | FirebaseDate;
	rejectedDate?: Date | FirebaseDate;
	rejectReason?: string;
	fiscalRegime: string;
	street: string;
	city: string;
	state: string; // State in mexico
	paymentReceiptLink?: string;
	fiscalSituationReceiptLink?: string;
	name: string; // Name entered in the form
	email: string;
	paymentForm: string; // In the form, "Metodo de pago"
	use: string; // Uso de factura
	rfc: string;
	zipCode: string;
	corporateName: string; // In form, "Razon Social"
	amount: number; // In form, "Monto de factura"
	netPrice?: number; // "Precio Neto" (subtotal antes de impuestos)
	ivaAmount?: number; // IVA trasladado; si falta, se toma amount − netPrice
	retIvaAmount?: number; // Retención de IVA (personas morales)
	retIsrAmount?: number; // Retención de ISR (personas morales)
	taxConfig?: InvoiceTaxConfig; // Impuestos elegidos al crear la solicitud en el panel
	status: string;
	matricula?: string;
	id?: string;
	canceledDate?: Date | FirebaseDate;
	ticketNumber?: number; //Numero de folio interno (api)
	paymentId?: string; // if the invoice is autogenerated, there will be a previously approved payment associated with it.
}

export interface InvoiceApiData {
	legal_name: string;
	tax_id: string;
	tax_system: string;
	zip: string;
	street: string;
	city: string;
	state: string;
	email: string;
	product_description: string;
	product_key: string;
	price: number;
	payment_form: string;
	use: string;
	// Solo personal: impuestos del concepto (p. ej. retenciones a personas morales).
	// Si faltan, Facturapi aplica IVA 16 % incluido en `price`, como siempre.
	tax_included?: boolean;
	taxes?: FacturapiTax[];
}

export async function checkForPendingInvoice(userId: string) {}

export async function getFbInvoiceByPaymentId(paymentId: string, userId?: string) {
	const constraints = [where("paymentId", "==", paymentId)];
	if (userId) constraints.push(where("userId", "==", userId));
	const q = query(collection(db, "invoices"), ...constraints, orderBy("createdAt", "desc"), limit(1));
	const qSnap = await getDocs(q);
	let invoice;
	if (!qSnap.empty) {
		invoice = qSnap.docs[0].data();
	} else {
		invoice = "";
	}
	return invoice;
}

export async function getInvoiceStatusByPaymentId(paymentId: string, userId?: string) {
	const constraints = [where("paymentId", "==", paymentId)];
	if (userId) constraints.push(where("userId", "==", userId));
	const q = query(collection(db, "invoices"), ...constraints, orderBy("createdAt", "desc"), limit(1));
	const qSnap = await getDocs(q);
	let status: string;
	if (!qSnap.empty) {
		status = qSnap.docs[0].data().status;
	} else {
		status = "";
	}
	return status;
}

export async function sendInvoiceByEmail(invoiceApi: any, invoiceForm: InvoiceData) {
	const pdfName: string = `FACTURA - ${invoiceForm.matricula ?? ""} - ${
		invoiceApi.folio_number
	}.pdf`;

	fetchInvoiceFile(invoiceApi.id, "pdf")
		.then(async (blob) => {
			const reader = new FileReader();
			let base64String;
			const currentDate = new Date();
			const day = currentDate.getDate().toString().padStart(2, "0");
			const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
			const year = currentDate.getFullYear();
			const now = `${day}/${month}/${year}`;
			reader.readAsDataURL(blob);
			reader.onloadend = function () {
				base64String = reader.result;
				const emailParams = {
					email: invoiceForm.email,
					filename: pdfName,
					content: base64String,
					name: invoiceForm.name,
					rfc: invoiceApi.customer.tax_id,
					ticket: invoiceApi.folio_number,
					corporateName: invoiceForm.corporateName.toUpperCase(),
					state: invoiceForm.state,
					fiscalRegime: invoiceForm.fiscalRegime,
					date: now,
					street: invoiceForm.street,
					invoiceEmail: invoiceForm.email,
					zipCode: invoiceForm.zipCode,
					use: invoiceForm.use,
					city: invoiceForm.city,
					paymentForm: paymentFormText[invoiceForm.paymentForm],
					netPrice: invoiceForm.netPrice,
					...(invoiceForm.ivaAmount !== undefined
						? { iva: invoiceForm.ivaAmount.toFixed(2) }
						: invoiceForm.netPrice
						? { iva: (invoiceForm.amount - invoiceForm.netPrice).toFixed(2) }
						: {}),
					iAmount: invoiceForm.amount,
					matricula: invoiceForm.matricula ?? "",
				};
				emailjs.send(
					"service_1l309zq",
					"template_vzsz96m",
					emailParams,
					"user_PPI0O8nofs9cbuJ3JRWlT"
				);
			};
		})
		.catch((error) => {
			console.error(error);
		});
}

export async function cancelInvoiceApi(invoiceApiId: string) {
	return callFacturapi("cancel", { invoiceApiId })
		.then((res) => {
			return res.invoice;
		})
		.catch((error) => {
			console.error(error);
			alert("Ocurrió un error al intentar cancelar la factura");
			return null;
		});
}

export async function downloadInvoicePDF(
	invoiceApiId: string,
	ticketNumber?: number,
	matricula?: string,
	format?: string
) {
	const pdfName: string = "FACTURA - " + (matricula ?? "") + " - " + (ticketNumber ?? "");
	const fileFormat = format === "xml" ? "xml" : "pdf";

	try {
		const blob = await fetchInvoiceFile(invoiceApiId, fileFormat);
		if (blob && blob.size > 0) {
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `${pdfName}.${fileFormat}`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		} else {
			alert("El archivo descargado está vacío.");
		}
	} catch (error: any) {
		console.error("Error al descargar el archivo:", error);
		if (error?.status === 404) {
			alert("La factura no fue encontrada o el formato solicitado no está disponible.");
		} else if (error?.status === 401 || error?.status === 403) {
			alert("No tienes permiso para descargar esta factura. Vuelve a iniciar sesión.");
		} else {
			alert("Por el momento este servicio no está disponible. Prueba más tarde.");
		}
	}
}

export async function addGenNameFB(invoiceId: string, genId: string) {
	const docRef = doc(db, "invoices", invoiceId);
	let tempGen = await getGenerationNameById(genId);
	if (tempGen) {
		let formattedGenName = tempGen.replace(
			"Programa de Alto Rendimiento en 8 Idiomas Simultáneos - ",
			""
		);
		await updateDoc(docRef, { userGenName: formattedGenName });
	}
}

export async function generateInvoice(data: InvoiceApiData, paymentId?: string) {
	// paymentId: el pago que se factura. El servidor lo exige cuando timbra un alumno.
	try {
		const { invoice } = await callFacturapi("create", { data, paymentId });
		return invoice;
	} catch (error: any) {
		console.log("error", error);
		alert(
			error?.status === 403 || error?.status === 409
				? `No se pudo generar la factura: ${error.message}`
				: "Ocurrió un error al intentar generar la factura, revisa que los campos de RFC, razón social, código postal y régimen fiscal sean correctos, o vuelve a intentar más tarde. \nTambién verificar que el uso de CFDI coincida con el régimen fiscal."
		);
		return null;
	}
}

export async function uploadInvoiceFireBase(data: InvoiceData) {
	try {
		const docRef = await addDoc(collection(db, "invoices"), data);
		return docRef.id;
	} catch (error) {
		// Para este punto Facturapi ya timbró: avisar para que no la vuelvan a generar.
		console.error("Error al registrar la factura en Firestore", error);
		alert(
			`La factura ${data.ApiId ? `(id ${data.ApiId}) ` : ""}ya se timbró, pero no se pudo registrar en el sitio. No la vuelvas a generar; avisa a la Academia.`
		);
		return null;
	}
}

export async function pendingInvoicesCount() {
	const q = query(collection(db, "invoices"), where("status", "==", "Pending"));
	const qSnap = await getDocs(q);
	if (qSnap.size > 0) {
		return qSnap.size;
	} else {
		return 0;
	}
}

export async function getAllInvoices() {
	// Get all invoices available
	const q = query(collection(db, "invoices"), orderBy("createdAt", "desc"));
	const qSnap = await getDocs(q);
	let invoices: any = [];
	if (qSnap.size > 0) {
		qSnap.docs.map((invoice, index) => {
			let data = { id: invoice.id, ...invoice.data() };
			invoices.push(data);
		});
		return invoices;
	} else {
		return;
	}
}

export async function invoicesLeftCount() {
	const { remaining } = await callFacturapi("remaining");
	return remaining as number;
}

export async function updateDbStatus(
	newStatus: string,
	id: string,
	rejectReasonText?: string,
	apiId?: string,
	TicketNumber?: number
) {
	const docRef = doc(db, "invoices", id);
	await updateDoc(docRef, {
		status: newStatus,
		...(rejectReasonText ? { rejectReason: rejectReasonText } : {}),
		...(newStatus === "Rejected" ? { rejectedDate: new Date() } : {}),
		...(newStatus === "Accepted"
			? { acceptedDate: new Date(), ApiId: apiId, ticketNumber: TicketNumber }
			: {}),
		...(newStatus === "Canceled" ? { acceptedDate: "", canceledDate: new Date() } : {}),
	}).then(() => {
		alert("Estatus actualizado con éxito");
	});
}

export async function updateDbPaymentStatus(newStatus: string, id: string) {
	// This function updates the status of a payment relating to its respective invoice.
	const docRef = doc(db, "paymentHistory", id);
	await updateDoc(docRef, {}).then(() => {
		alert("Estatus actualizado con éxito");
	});
}

export function firebaseDateFormatter(date: FirebaseDate) {
	let tempDate = new Date(date.seconds * 1000 + date.nanoseconds / 1000000);
	let formattedDate = tempDate
		.toLocaleString("es-ES", dateOptions)
		.replace(/(\d+) de (\w+) de (\d+)/, "$1 de $2 de $3");
	return formattedDate;
}
export function firebaseDateTimeFormatter(onlyTime: boolean, date: FirebaseDate) {
	let tempDate = new Date(date.seconds * 1000 + date.nanoseconds / 1000000);

	let dateOptions: Intl.DateTimeFormatOptions = {
		day: "numeric",
		month: "long",
		year: "numeric",
	};
	let timeOptions: Intl.DateTimeFormatOptions = {
		hour: "numeric",
		minute: "numeric",
		hour12: true,
	};

	let formattedDate = tempDate.toLocaleString("es-ES", dateOptions);
	let formattedTime = tempDate.toLocaleString("es-ES", timeOptions);
	if (onlyTime === false) {
		return `${formattedDate} - ${formattedTime}`;
	} else {
		return `${formattedTime}`;
	}
}
